"use client";

import React, { useState, useEffect, useCallback } from "react";
import { doorstepServiceSteps } from "@/lib/data";

const API_BASE = process.env.NEXT_PUBLIC_API_URL || "";

interface Slot {
  id: number;
  slot_type: "fixed" | "custom";
  slot_template_id: string | null;
  template_label: string | null;
  start_time: string;
  end_time: string;
  applies_to: "date" | "always";
  slot_date: string | null;
  capacity: number;
  booked_count: number;
  remaining: number;
  status: "available" | "full" | "blocked";
}

function formatTime(t: string): string {
  const [h, m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const hour = h % 12 || 12;
  return `${hour}:${m.toString().padStart(2, "0")} ${ampm}`;
}

function toYMD(y: number, mo: number, d: number): string {
  return `${y}-${String(mo + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

function normalizeYMD(input: string): string {
  return String(input || "").slice(0, 10);
}

function todayYMD(): string {
  const n = new Date();
  return toYMD(n.getFullYear(), n.getMonth(), n.getDate());
}

function sanitizeName(value: string): string {
  return value.replace(/[^a-zA-Z\s]/g, "").replace(/\s{2,}/g, " ");
}

function sanitizePhone(value: string): string {
  return value.replace(/\D/g, "").slice(0, 10);
}

function Spinner({
  size = 16,
  color = "currentColor",
}: {
  size?: number;
  color?: string;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className="animate-spin"
      style={{ color }}
    >
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="3"
        strokeOpacity="0.25"
      />
      <path
        d="M12 2a10 10 0 0110 10"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

function SlotSkeleton() {
  return (
    <div className="animate-pulse space-y-3">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="h-[64px] bg-neutral-100 rounded-2xl border border-neutral-200"
        />
      ))}
    </div>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="block mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-700">
      {children}
    </label>
  );
}

function TextInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className="w-full h-12 rounded-xl border border-neutral-300 bg-white px-4 text-[15px] text-black outline-none transition focus:border-[#c8a96e] focus:ring-4 focus:ring-[#c8a96e]/10 placeholder:text-neutral-400"
    />
  );
}

function SlotList({
  date,
  selectedSlotId,
  onSelect,
}: {
  date: string;
  selectedSlotId: number | null;
  onSelect: (id: number) => void;
}) {
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const doFetch = useCallback(async (d: string) => {
    setLoading(true);
    setError("");
    setSlots([]);

    try {
      const ymd = normalizeYMD(d);
      const res = await fetch(
        `${API_BASE}/api/appointments/slots/available?date=${encodeURIComponent(ymd)}`
      );
      const ct = res.headers.get("content-type") || "";
      if (!ct.includes("application/json")) {
        throw new Error(`Server error (${res.status})`);
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to load slots");
      setSlots(data.data || []);
    } catch (e: any) {
      setError(e.message || "Could not load slots");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (date) doFetch(date);
    else {
      setSlots([]);
      setError("");
    }
  }, [date, doFetch]);

  if (!date) return null;

  return (
    <div className="mt-5 space-y-3">
      <div className="flex items-center justify-between mb-1">
        <p className="text-[11px] font-semibold text-neutral-700 uppercase tracking-[0.18em]">
          Available Slots
        </p>
        {!loading && slots.length > 0 && (
          <span className="text-xs text-neutral-400">
            {slots.length} slot{slots.length !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      {loading && <SlotSkeleton />}

      {!loading && error && (
        <div className="flex items-center justify-between gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
          <span>⚠ {error}</span>
          <button
            type="button"
            onClick={() => doFetch(date)}
            className="font-semibold underline underline-offset-2 whitespace-nowrap"
          >
            Retry
          </button>
        </div>
      )}

      {!loading && !error && slots.length === 0 && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-center">
          <p className="text-sm font-semibold text-amber-700">No slots on this date</p>
          <p className="text-xs text-amber-600 mt-1">Pick another available date from the calendar.</p>
        </div>
      )}

      {!loading &&
        !error &&
        slots.map((slot) => {
          const isSelected = selectedSlotId === slot.id;
          const left = slot.remaining ?? slot.capacity - slot.booked_count;
          const hot = left <= 2;

          return (
            <button
              key={slot.id}
              type="button"
              onClick={() => onSelect(slot.id)}
              className={`w-full text-left px-4 py-3 rounded-2xl border transition-all duration-150 ${
                isSelected
                  ? "border-[#c8a96e] bg-[#fffaf2] ring-2 ring-[#c8a96e]/20 shadow-sm"
                  : "border-neutral-200 bg-white hover:border-[#c8a96e] hover:bg-[#fffdf8]"
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[14px] font-semibold text-[#1a1a1a]">
                    {formatTime(slot.start_time)} – {formatTime(slot.end_time)}
                  </p>
                  {slot.template_label && (
                    <p className="mt-1 text-xs text-neutral-500">{slot.template_label}</p>
                  )}
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <span
                    className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${
                      hot
                        ? "bg-amber-100 text-amber-700"
                        : "bg-green-100 text-green-700"
                    }`}
                  >
                    {left === 1 ? "Last spot!" : `${left} left`}
                  </span>

                  {isSelected && (
                    <span className="w-5 h-5 rounded-full bg-[#c8a96e] flex items-center justify-center">
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                        <path
                          d="M2 5l2.3 2.3L8 3"
                          stroke="white"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
    </div>
  );
}

function CalendarPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (ymd: string) => void;
}) {
  const TODAY = todayYMD();
  const now = new Date();

  const [viewYear, setViewYear] = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth());
  const [datesWithSlots, setDatesWithSlots] = useState<Set<string>>(new Set());
  const [loadingMonth, setLoadingMonth] = useState(false);

  const MONTH_NAMES = [
    "January","February","March","April","May","June",
    "July","August","September","October","November","December",
  ];
  const DAY_NAMES = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

  const scanMonth = useCallback(
    async (year: number, month: number) => {
      setLoadingMonth(true);
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const found = new Set<string>();

      const promises = Array.from({ length: daysInMonth }, (_, i) => {
        const d = toYMD(year, month, i + 1);
        if (d < TODAY) return Promise.resolve({ d, count: 0 });
        return fetch(
          `${API_BASE}/api/appointments/slots/available?date=${encodeURIComponent(d)}`
        )
          .then((r) => r.json())
          .then((data) => ({ d, count: data.data?.length ?? 0 }))
          .catch(() => ({ d, count: 0 }));
      });

      const results = await Promise.all(promises);
      results.forEach(({ d, count }) => {
        if (count > 0) found.add(d);
      });

      setDatesWithSlots(found);
      setLoadingMonth(false);
    },
    [TODAY]
  );

  useEffect(() => {
    scanMonth(viewYear, viewMonth);
  }, [viewYear, viewMonth, scanMonth]);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewYear((y) => y - 1); setViewMonth(11); }
    else setViewMonth((m) => m - 1);
  };

  const nextMonth = () => {
    if (viewMonth === 11) { setViewYear((y) => y + 1); setViewMonth(0); }
    else setViewMonth((m) => m + 1);
  };

  const isCurrentMonth = viewYear === now.getFullYear() && viewMonth === now.getMonth();
  const firstDayOfWeek = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  return (
    <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-4 border-b border-neutral-200">
        <button
          type="button"
          onClick={prevMonth}
          disabled={isCurrentMonth}
          className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-neutral-100 transition disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M9 11L5 7l4-4" stroke="#1a1a1a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        <div className="text-center">
          <p className="text-sm font-semibold text-[#1a1a1a]">
            {MONTH_NAMES[viewMonth]} {viewYear}
          </p>
          {loadingMonth ? (
            <p className="text-xs text-[#c8a96e] mt-1 flex items-center justify-center gap-1">
              <Spinner size={10} color="#c8a96e" /> checking availability…
            </p>
          ) : (
            <p className="text-xs text-neutral-400 mt-1">
              {datesWithSlots.size} date{datesWithSlots.size !== 1 ? "s" : ""} with slots
            </p>
          )}
        </div>

        <button
          type="button"
          onClick={nextMonth}
          className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-neutral-100 transition"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M5 3l4 4-4 4" stroke="#1a1a1a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      <div className="grid grid-cols-7 px-3 pt-3 pb-2">
        {DAY_NAMES.map((d) => (
          <div key={d} className="text-center text-[11px] font-semibold text-neutral-400 uppercase py-1">
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 px-3 pb-4 gap-y-1.5">
        {Array.from({ length: firstDayOfWeek }).map((_, i) => (
          <div key={`e${i}`} />
        ))}

        {Array.from({ length: daysInMonth }, (_, i) => {
          const day = i + 1;
          const ymd = toYMD(viewYear, viewMonth, day);
          const isPast = ymd < TODAY;
          const isToday = ymd === TODAY;
          const isSelected = ymd === value;
          const hasSlots = datesWithSlots.has(ymd);

          return (
            <button
              key={day}
              type="button"
              disabled={isPast || (!hasSlots && !loadingMonth)}
              onClick={() => onChange(ymd)}
              title={
                isPast ? "Past date"
                : hasSlots ? "Slots available"
                : loadingMonth ? "Checking…"
                : "No slots"
              }
              className={`relative h-10 w-full rounded-xl text-sm font-medium transition-all ${
                isPast
                  ? "text-neutral-200 cursor-not-allowed"
                  : isSelected
                  ? "bg-[#c8a96e] text-white shadow-sm"
                  : isToday && hasSlots
                  ? "bg-[#fffaf2] text-[#1a1a1a] ring-1 ring-[#c8a96e]"
                  : hasSlots
                  ? "text-[#1a1a1a] hover:bg-[#fffaf2] hover:ring-1 hover:ring-[#c8a96e]"
                  : loadingMonth
                  ? "text-neutral-300 cursor-wait"
                  : "text-neutral-300 cursor-not-allowed"
              }`}
            >
              {day}
              {hasSlots && !isSelected && !isPast && (
                <span className="absolute bottom-[4px] left-1/2 -translate-x-1/2 w-[5px] h-[5px] rounded-full bg-[#c8a96e]" />
              )}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-4 px-4 py-3 border-t border-neutral-200 bg-neutral-50">
        <span className="flex items-center gap-1.5 text-xs text-neutral-500">
          <span className="w-[6px] h-[6px] rounded-full bg-[#c8a96e] inline-block" />
          Slots available
        </span>
        <span className="flex items-center gap-1.5 text-xs text-neutral-500">
          <span className="w-[6px] h-[6px] rounded-full bg-neutral-300 inline-block" />
          No slots
        </span>
      </div>
    </div>
  );
}

function DateSlotPicker({
  date,
  selectedSlotId,
  onDateChange,
  onSlotSelect,
}: {
  date: string;
  selectedSlotId: number | null;
  onDateChange: (ymd: string) => void;
  onSlotSelect: (id: number) => void;
}) {
  const [calendarOpen, setCalendarOpen] = useState(false);

  const MONTH_NAMES = [
    "January","February","March","April","May","June",
    "July","August","September","October","November","December",
  ];

  const handleDateSelect = (ymd: string) => {
    onDateChange(ymd);
    setCalendarOpen(false);
  };

  const formatSelected = (ymd: string) => {
    if (!ymd) return null;
    const [y, m, d] = ymd.split("-").map(Number);
    const dow = new Date(y, m - 1, d).toLocaleDateString("en-US", { weekday: "short" });
    return `${dow}, ${MONTH_NAMES[m - 1]} ${d}, ${y}`;
  };

  const displayDate = formatSelected(date);

  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={() => setCalendarOpen((o) => !o)}
        className={`w-full h-12 flex items-center justify-between px-4 rounded-xl border transition-all ${
          calendarOpen
            ? "border-[#c8a96e] bg-[#fffaf2] ring-4 ring-[#c8a96e]/10"
            : date
            ? "border-[#c8a96e] bg-[#fffaf2]"
            : "border-neutral-300 bg-white hover:border-[#c8a96e]"
        }`}
      >
        <div className="flex items-center gap-2.5">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            className={date ? "text-[#c8a96e]" : "text-neutral-400"}
          >
            <rect x="1.5" y="2.5" width="13" height="12" rx="2" stroke="currentColor" strokeWidth="1.3" />
            <path d="M1.5 6.5h13" stroke="currentColor" strokeWidth="1.3" />
            <path d="M5 1.5v2M11 1.5v2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
            <circle cx="5" cy="9.5" r="0.8" fill="currentColor" />
            <circle cx="8" cy="9.5" r="0.8" fill="currentColor" />
            <circle cx="11" cy="9.5" r="0.8" fill="currentColor" />
            <circle cx="5" cy="12.5" r="0.8" fill="currentColor" />
            <circle cx="8" cy="12.5" r="0.8" fill="currentColor" />
          </svg>
          <span className={`text-[14px] ${date ? "text-[#1a1a1a] font-medium" : "text-neutral-400"}`}>
            {displayDate ?? "Select a date"}
          </span>
        </div>

        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          className={`transition-transform duration-200 text-neutral-400 ${calendarOpen ? "rotate-180" : ""}`}
        >
          <path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {calendarOpen && (
        <div className="animate-in fade-in slide-in-from-top-1 duration-150">
          <CalendarPicker value={date} onChange={handleDateSelect} />
        </div>
      )}

      {date && (
        <SlotList
          date={date}
          selectedSlotId={selectedSlotId}
          onSelect={onSlotSelect}
        />
      )}
    </div>
  );
}

export default function BookVisitPage() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    date: "",
    description: "",
  });

  const [selectedSlotId, setSelectedSlotId] = useState<number | null>(null);
  const [isBooked, setIsBooked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleDateChange = (ymd: string) => {
    setFormData((p) => ({ ...p, date: normalizeYMD(ymd) }));
    setSelectedSlotId(null);
  };

  const handleBooking = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;

    const cleanName = formData.name.trim();
    const cleanPhone = formData.phone.trim();

    if (!selectedSlotId) {
      setError("Please select a time slot.");
      return;
    }

    if (!formData.date) {
      setError("Please select a date.");
      return;
    }

    if (!/^[A-Za-z\s]+$/.test(cleanName)) {
      setError("Full Name should contain only letters and spaces.");
      return;
    }

    if (!/^[0-9]{10}$/.test(cleanPhone)) {
      setError("Phone number must be exactly 10 digits.");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const res = await fetch(`${API_BASE}/api/appointments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          slot_id: selectedSlotId,
          name: cleanName,
          phone: cleanPhone,
          email: formData.email.trim(),
          address: formData.address.trim(),
          date: normalizeYMD(formData.date),
          description: formData.description.trim(),
        }),
      });

      const ct = res.headers.get("content-type") || "";
      const data = ct.includes("application/json")
        ? await res.json()
        : { message: `Server error (${res.status})` };

      if (!res.ok) {
        setError(data.message || "Something went wrong.");
        return;
      }

      setIsBooked(true);
    } catch {
      setError("Network error. Please check your connection.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#fcfcfb] text-[#171717] pb-16 py-22">
      <section className="relative w-full h-[34vh] md:h-[40vh] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1441986300917-64674bd600d8?q=80&w=2070&auto=format&fit=crop"
          alt="Studio Hero"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/35" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white px-6 text-center">
          <h1 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl italic leading-none">
            Book Appointment
          </h1>
          <p className="mt-3 text-[11px] md:text-xs uppercase tracking-[0.28em] text-white/85">
            Precision. Craftsmanship. Your vision.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto py-10 md:py-14 px-4 md:px-6 grid grid-cols-1 lg:grid-cols-[0.95fr_1.05fr] gap-10 md:gap-12 items-start">
        <div className="pt-2 md:pt-6">
          <p className="text-3xl md:text-4xl text-[#1a1a1a] font-['Cormorant_Garamond'] italic leading-snug">
            Fashion is an art of expression. Our studio is where that expression takes shape.
          </p>
          <div className="mt-6 h-px w-16 bg-[#c8a96e]" />
          <p className="mt-5 text-sm leading-6 text-neutral-600 max-w-md">
            Pick a date with availability, choose a time slot, and complete your appointment request below.
          </p>
          <p className="mt-5 text-sm leading-6 text-neutral-600 max-w-md">
            <span className="font-semibold text-[#1a1a1a]">Note:</span>{" "}
            <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
              Our services are currently available only in the NCR region.
            </span>
          </p>
        </div>

        <div className="w-full max-w-[560px] lg:justify-self-end">
          {!isBooked ? (
            <form
              onSubmit={handleBooking}
              className="space-y-6 bg-white p-6 md:p-8 shadow-xl rounded-3xl border border-neutral-200"
            >
              <div>
                <FieldLabel>Full Name</FieldLabel>
                <TextInput
                  type="text"
                  required
                  placeholder="Your full name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: sanitizeName(e.target.value) })
                  }
                  pattern="[A-Za-z\s]+"
                  title="Name should contain only letters and spaces"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <FieldLabel>Phone</FieldLabel>
                  <TextInput
                    type="tel"
                    required
                    maxLength={10}
                    inputMode="numeric"
                    pattern="[0-9]{10}"
                    placeholder="+91 XXX XXX XXXX"
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData({ ...formData, phone: sanitizePhone(e.target.value) })
                    }
                    title="Phone number must be exactly 10 digits"
                  />
                </div>

                <div>
                  <FieldLabel>Email</FieldLabel>
                  <TextInput
                    type="email"
                    required
                    placeholder="email@address.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <FieldLabel>Preferred Date & Time Slot</FieldLabel>
                <DateSlotPicker
                  date={formData.date}
                  selectedSlotId={selectedSlotId}
                  onDateChange={handleDateChange}
                  onSlotSelect={setSelectedSlotId}
                />
              </div>

              <div>
                <FieldLabel>Address</FieldLabel>
                <TextInput
                  type="text"
                  required
                  placeholder="Your full address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>

              <div>
                <FieldLabel>What brings you here?</FieldLabel>
                <textarea
                  rows={4}
                  placeholder="Tell us about your vision or requirements…"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full min-h-[110px] rounded-xl border border-neutral-300 bg-white px-4 py-3 text-[14px] text-black outline-none resize-none transition focus:border-[#c8a96e] focus:ring-4 focus:ring-[#c8a96e]/10 placeholder:text-neutral-400"
                />
              </div>

              {error && (
                <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading || !selectedSlotId || !formData.date}
                className="w-full h-12 bg-[#051173] text-white text-[13px] font-semibold uppercase tracking-[0.18em] hover:bg-[#c8a96e] transition rounded-xl disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Spinner size={16} color="white" /> Submitting…
                  </span>
                ) : !formData.date ? (
                  "Confirm Appointment"
                ) : !selectedSlotId ? (
                  "Select a Time Slot"
                ) : (
                  "Confirm Appointment"
                )}
              </button>
            </form>
          ) : (
            <div className="bg-white p-8 md:p-10 shadow-xl rounded-3xl border border-green-200 text-center">
              <div className="text-4xl mb-4">✨</div>
              <h2 className="text-2xl font-semibold text-[#1a1a1a] mb-2">
                Appointment Confirmed!
              </h2>
              <p className="text-neutral-600 text-sm">
                A confirmation email has been sent to{" "}
                <span className="font-semibold text-neutral-800">{formData.email}</span>.
              </p>
              <p className="text-neutral-400 text-xs mt-2">Please arrive 10 minutes early.</p>
              <button
                onClick={() => {
                  setIsBooked(false);
                  setFormData({
                    name: "",
                    phone: "",
                    email: "",
                    address: "",
                    date: "",
                    description: "",
                  });
                  setSelectedSlotId(null);
                }}
                className="mt-8 text-[11px] font-semibold text-[#c8a96e] uppercase tracking-[0.18em] underline underline-offset-4"
              >
                Book another appointment
              </button>
            </div>
          )}
        </div>
      </section>

      <section className="bg-[#f8f7f4] py-16 md:py-20 px-4 md:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12 md:mb-14">
            <span className="text-[11px] font-semibold text-[#c8a96e] uppercase tracking-[0.28em]">
              The Process
            </span>
            <h2 className="text-3xl md:text-4xl font-['Cormorant_Garamond'] italic text-[#121826] mt-3">
              How Doorstep Stitching Works
            </h2>
            <div className="w-16 h-px bg-[#c8a96e] mx-auto mt-5" />
          </div>

          <div className="relative">
            <div className="hidden md:block absolute left-[23px] top-0 bottom-0 w-px bg-[#d9c39c]" />
            <div className="space-y-10">
              {doorstepServiceSteps.map((step: any, index: number) => (
                <div key={index} className="relative flex gap-5 md:gap-6 items-start group">
                  <div className="relative z-10 flex-shrink-0 w-12 h-12 rounded-full bg-white border border-neutral-200 flex items-center justify-center text-[#121826] font-semibold text-base shadow-sm">
                    {index + 1}
                  </div>
                  <div className="pt-1">
                    <h3 className="text-lg font-semibold text-[#121826] mb-1.5">
                      {step.title}
                    </h3>
                    <p className="text-sm leading-6 text-neutral-600 max-w-2xl">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}